<script setup lang="ts">
import { ref, inject, onMounted, computed, watch } from 'vue'
import type { Ref } from 'vue'
import { useRouter } from 'vue-router'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { toast } from 'vue-sonner'
import {
	Palette, Type, Image, Layout, Upload, Globe, Eye, Save,
	ChevronLeft, ChevronRight, Loader2, Sparkles, Monitor,
	Smartphone, GalleryHorizontalEnd, Trophy, Video, FileText,
	Users, LayoutTemplate, Settings2, Check, Plus, Trash2,
} from 'lucide-vue-next'
import { useApplications } from '@/services/Applications'
import type { ApplicationInfo } from '@/types/Applications'
import axios from 'axios'

const router = useRouter()
const vhostId = inject<Ref<string>>('vhostId', ref(''))
const { getApplicationList } = useApplications()

// ─── State ───
const loading = ref(true)
const saving = ref(false)
const applications = ref<ApplicationInfo[]>([])
const selectedAppId = ref<string>('')
const currentStep = ref(0)
const previewMode = ref<'desktop' | 'mobile'>('desktop')

// ─── Template choices ───
interface AppTemplate {
	id: string
	name: string
	description: string
	icon: typeof GalleryHorizontalEnd
	color: string
}

const templateChoices: AppTemplate[] = [
	{ id: 'gallery', name: 'Media Gallery', description: 'Showcase photos and videos in a beautiful grid layout', icon: GalleryHorizontalEnd, color: 'bg-blue-500/10 text-blue-500' },
	{ id: 'upload', name: 'Upload Portal', description: 'Allow users to submit content with custom forms', icon: Upload, color: 'bg-green-500/10 text-green-500' },
	{ id: 'contest', name: 'Contest / Campaign', description: 'Run contests with voting, submissions, and prizes', icon: Trophy, color: 'bg-amber-500/10 text-amber-500' },
	{ id: 'video', name: 'Video Portal', description: 'Stream and manage video content', icon: Video, color: 'bg-purple-500/10 text-purple-500' },
	{ id: 'landing', name: 'Landing Page', description: 'Simple informational page with upload option', icon: FileText, color: 'bg-rose-500/10 text-rose-500' },
	{ id: 'community', name: 'Community Hub', description: 'User profiles, submissions, and social features', icon: Users, color: 'bg-teal-500/10 text-teal-500' },
]

// ─── Builder settings model ───
const config = ref({
	template: '' as string,
	siteName: '',
	tagline: '',
	logoUrl: '',
	primaryColor: '#3b82f6',
	backgroundColor: '#ffffff',
	textColor: '#1f2937',
	accentColor: '#8b5cf6',
	fontFamily: 'Inter',
	heroEnabled: true,
	heroTitle: '',
	heroSubtitle: '',
	heroImageUrl: '',
	galleryEnabled: true,
	galleryLayout: 'grid' as 'grid' | 'masonry' | 'carousel',
	galleryColumns: 3,
	uploadEnabled: true,
	uploadTitle: 'Submit Your Content',
	uploadDescription: '',
	uploadMaxSize: 100,
	uploadAllowedTypes: ['image', 'video'] as string[],
	uploadRequireLogin: false,
	footerEnabled: true,
	footerText: '',
	footerLinks: [] as { label: string; url: string }[],
	navigationEnabled: true,
	navigationItems: [] as { label: string; section: string }[],
	registrationEnabled: false,
	registrationFields: ['name', 'email'] as string[],
	socialLinksEnabled: false,
	socialLinks: { facebook: '', twitter: '', instagram: '', youtube: '' },
	seoTitle: '',
	seoDescription: '',
	analyticsId: '',
	customCss: '',
})

// ─── Steps ───
interface WizardStep {
	label: string
	icon: typeof Palette
	description: string
}

const steps: WizardStep[] = [
	{ label: 'Template', icon: LayoutTemplate, description: 'Choose a starting point' },
	{ label: 'Branding', icon: Palette, description: 'Colors, logo, and fonts' },
	{ label: 'Content', icon: Type, description: 'Hero section and text' },
	{ label: 'Gallery', icon: Image, description: 'Media display settings' },
	{ label: 'Uploads', icon: Upload, description: 'Submission settings' },
	{ label: 'Sections', icon: Layout, description: 'Footer and navigation' },
	{ label: 'Publish', icon: Globe, description: 'Review and go live' },
]

const canGoNext = computed(() => {
	if (currentStep.value === 0) return config.value.template !== ''
	return true
})

const fontOptions = [
	{ value: 'Inter', label: 'Inter (Modern)' },
	{ value: 'Roboto', label: 'Roboto (Clean)' },
	{ value: 'Open Sans', label: 'Open Sans (Friendly)' },
	{ value: 'Lato', label: 'Lato (Professional)' },
	{ value: 'Playfair Display', label: 'Playfair (Elegant)' },
	{ value: 'Montserrat', label: 'Montserrat (Bold)' },
]

const layoutOptions = [
	{ value: 'grid', label: 'Grid' },
	{ value: 'masonry', label: 'Masonry' },
	{ value: 'carousel', label: 'Carousel' },
]

// ─── Methods ───
const selectTemplate = (tpl: AppTemplate) => {
	config.value.template = tpl.id
	// Pre-fill sensible defaults based on template
	if (tpl.id === 'gallery') {
		config.value.galleryEnabled = true
		config.value.uploadEnabled = false
		config.value.heroTitle = 'Our Gallery'
	} else if (tpl.id === 'upload') {
		config.value.uploadEnabled = true
		config.value.galleryEnabled = false
		config.value.heroTitle = 'Submit Your Content'
	} else if (tpl.id === 'contest') {
		config.value.uploadEnabled = true
		config.value.galleryEnabled = true
		config.value.registrationEnabled = true
		config.value.heroTitle = 'Enter to Win'
	} else if (tpl.id === 'video') {
		config.value.galleryEnabled = true
		config.value.galleryLayout = 'grid'
		config.value.heroTitle = 'Video Portal'
	} else if (tpl.id === 'landing') {
		config.value.galleryEnabled = false
		config.value.uploadEnabled = true
		config.value.heroTitle = 'Welcome'
	} else if (tpl.id === 'community') {
		config.value.registrationEnabled = true
		config.value.galleryEnabled = true
		config.value.uploadEnabled = true
		config.value.heroTitle = 'Join Our Community'
	}
}

const addFooterLink = () => {
	config.value.footerLinks.push({ label: '', url: '' })
}

const removeFooterLink = (index: number) => {
	config.value.footerLinks.splice(index, 1)
}

const addNavItem = () => {
	config.value.navigationItems.push({ label: '', section: 'gallery' })
}

const removeNavItem = (index: number) => {
	config.value.navigationItems.splice(index, 1)
}

const saveConfig = async () => {
	if (!selectedAppId.value) {
		toast.error('No application selected')
		return
	}
	saving.value = true
	try {
		await axios.post('/services/json', {
			method: 'vhost.updateApplicationSettings',
			appId: selectedAppId.value,
			settings: JSON.stringify(config.value),
		})
		toast.success('Application saved successfully')
	} catch (err) {
		toast.error('Failed to save application')
		console.error(err)
	} finally {
		saving.value = false
	}
}

const publishApp = async () => {
	await saveConfig()
	if (!saving.value) {
		toast.success('Application published!')
	}
}

const loadExistingConfig = async (appId: string) => {
	try {
		const response = await axios.post('/services/json', {
			method: 'vhost.getApplicationSettings',
			appId,
		})
		if (response.data?.data?.settings) {
			const existing = JSON.parse(response.data.data.settings)
			Object.assign(config.value, existing)
			if (config.value.template) {
				currentStep.value = 1 // skip template selection if already chosen
			}
		}
	} catch {
		// No existing config — fresh start
	}
}

async function loadApplications() {
	try {
		loading.value = true
		applications.value = await getApplicationList(vhostId.value)
		if (applications.value.length > 0) {
			selectedAppId.value = applications.value[0].id
			await loadExistingConfig(selectedAppId.value)
		}
	} catch (err) {
		toast.error('Failed to load applications')
	} finally {
		loading.value = false
	}
}

watch(selectedAppId, async (newId) => {
	if (newId) {
		await loadExistingConfig(newId)
	}
})

onMounted(loadApplications)
</script>

<template>
	<div class="h-full flex flex-col">
		<!-- Loading -->
		<div v-if="loading" class="flex items-center justify-center py-20">
			<Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
		</div>

		<template v-else>
			<!-- Header -->
			<div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 border-b bg-background shrink-0">
				<div>
					<h1 class="text-lg font-medium">Application Builder</h1>
					<p class="text-sm text-muted-foreground">Build your site visually — no coding required</p>
				</div>
				<div class="flex items-center gap-2 w-full sm:w-auto">
					<Select v-if="applications.length > 1" :model-value="selectedAppId" @update:model-value="(v: string) => selectedAppId = v">
						<SelectTrigger class="w-full sm:w-[200px]">
							<SelectValue placeholder="Select app" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="app in applications" :key="app.id" :value="app.id">{{ app.name }}</SelectItem>
						</SelectContent>
					</Select>
					<Button variant="outline" size="sm" @click="saveConfig" :disabled="saving" class="gap-1.5">
						<Loader2 v-if="saving" class="h-4 w-4 animate-spin" />
						<Save v-else class="h-4 w-4" />
						<span class="hidden sm:inline">Save</span>
					</Button>
				</div>
			</div>

			<!-- Step indicator — desktop horizontal -->
			<div class="hidden md:flex items-center gap-1 px-4 py-3 border-b bg-muted/20 overflow-x-auto shrink-0">
				<button
					v-for="(step, idx) in steps"
					:key="idx"
					class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
					:class="currentStep === idx
						? 'bg-primary text-primary-foreground'
						: idx < currentStep
							? 'bg-primary/10 text-primary'
							: 'text-muted-foreground hover:bg-muted'"
					@click="(idx <= currentStep || canGoNext) && (currentStep = idx)"
				>
					<Check v-if="idx < currentStep" class="h-3.5 w-3.5" />
					<component v-else :is="step.icon" class="h-3.5 w-3.5" />
					{{ step.label }}
				</button>
			</div>

			<!-- Step indicator — mobile select -->
			<div class="md:hidden px-3 py-2 border-b shrink-0">
				<Select :model-value="String(currentStep)" @update:model-value="(v: string) => currentStep = Number(v)">
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="(step, idx) in steps" :key="idx" :value="String(idx)">
							{{ idx + 1 }}. {{ step.label }} — {{ step.description }}
						</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<!-- Step content -->
			<div class="flex-1 overflow-y-auto p-3 md:p-6">
				<!-- ═══ Step 0: Template Selection ═══ -->
				<div v-if="currentStep === 0" class="max-w-4xl mx-auto">
					<h2 class="text-lg font-medium mb-1">Choose a Template</h2>
					<p class="text-sm text-muted-foreground mb-6">Pick a starting point that matches your goals. You can customize everything afterwards.</p>
					<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						<Card
							v-for="tpl in templateChoices"
							:key="tpl.id"
							class="cursor-pointer transition-all hover:shadow-md"
							:class="config.template === tpl.id ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/40'"
							@click="selectTemplate(tpl)"
						>
							<CardContent class="p-5">
								<div class="flex items-start gap-3">
									<div class="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" :class="tpl.color">
										<component :is="tpl.icon" class="h-5 w-5" />
									</div>
									<div>
										<h3 class="text-sm font-semibold">{{ tpl.name }}</h3>
										<p class="text-xs text-muted-foreground mt-1">{{ tpl.description }}</p>
									</div>
								</div>
								<div v-if="config.template === tpl.id" class="mt-3 flex items-center gap-1.5 text-xs text-primary font-medium">
									<Check class="h-3.5 w-3.5" /> Selected
								</div>
							</CardContent>
						</Card>
					</div>
				</div>

				<!-- ═══ Step 1: Branding ═══ -->
				<div v-if="currentStep === 1" class="max-w-2xl mx-auto space-y-6">
					<div>
						<h2 class="text-lg font-medium mb-1">Branding</h2>
						<p class="text-sm text-muted-foreground">Set your colors, logo, and typography to match your brand.</p>
					</div>

					<div class="space-y-4">
						<div class="grid gap-2">
							<Label>Site Name</Label>
							<Input v-model="config.siteName" placeholder="My Awesome Site" />
						</div>

						<div class="grid gap-2">
							<Label>Tagline</Label>
							<Input v-model="config.tagline" placeholder="A short description of your site" />
						</div>

						<div class="grid gap-2">
							<Label>Logo URL</Label>
							<Input v-model="config.logoUrl" placeholder="https://example.com/logo.png" />
						</div>

						<Separator />

						<div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
							<div class="grid gap-2">
								<Label class="text-xs">Primary Color</Label>
								<div class="flex items-center gap-2">
									<input type="color" v-model="config.primaryColor" class="h-8 w-8 rounded cursor-pointer border" />
									<Input v-model="config.primaryColor" class="text-xs font-mono" />
								</div>
							</div>
							<div class="grid gap-2">
								<Label class="text-xs">Background</Label>
								<div class="flex items-center gap-2">
									<input type="color" v-model="config.backgroundColor" class="h-8 w-8 rounded cursor-pointer border" />
									<Input v-model="config.backgroundColor" class="text-xs font-mono" />
								</div>
							</div>
							<div class="grid gap-2">
								<Label class="text-xs">Text Color</Label>
								<div class="flex items-center gap-2">
									<input type="color" v-model="config.textColor" class="h-8 w-8 rounded cursor-pointer border" />
									<Input v-model="config.textColor" class="text-xs font-mono" />
								</div>
							</div>
							<div class="grid gap-2">
								<Label class="text-xs">Accent</Label>
								<div class="flex items-center gap-2">
									<input type="color" v-model="config.accentColor" class="h-8 w-8 rounded cursor-pointer border" />
									<Input v-model="config.accentColor" class="text-xs font-mono" />
								</div>
							</div>
						</div>

						<Separator />

						<div class="grid gap-2">
							<Label>Font Family</Label>
							<Select v-model="config.fontFamily">
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-for="font in fontOptions" :key="font.value" :value="font.value">
										{{ font.label }}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>

					<!-- Live preview swatch -->
					<Card class="overflow-hidden">
						<div class="p-4 text-center" :style="{ backgroundColor: config.backgroundColor, color: config.textColor, fontFamily: config.fontFamily }">
							<div class="text-lg font-bold" :style="{ color: config.primaryColor }">{{ config.siteName || 'Your Site Name' }}</div>
							<div class="text-sm mt-1 opacity-70">{{ config.tagline || 'Your tagline here' }}</div>
							<div class="mt-3 inline-block px-4 py-1.5 rounded-md text-sm text-white" :style="{ backgroundColor: config.primaryColor }">
								Sample Button
							</div>
						</div>
					</Card>
				</div>

				<!-- ═══ Step 2: Content / Hero ═══ -->
				<div v-if="currentStep === 2" class="max-w-2xl mx-auto space-y-6">
					<div>
						<h2 class="text-lg font-medium mb-1">Content</h2>
						<p class="text-sm text-muted-foreground">Configure the hero section that greets your visitors.</p>
					</div>

					<div class="flex items-center justify-between">
						<Label>Enable Hero Section</Label>
						<Switch v-model:checked="config.heroEnabled" />
					</div>

					<template v-if="config.heroEnabled">
						<div class="space-y-4">
							<div class="grid gap-2">
								<Label>Hero Title</Label>
								<Input v-model="config.heroTitle" placeholder="Welcome to our site" />
							</div>
							<div class="grid gap-2">
								<Label>Hero Subtitle</Label>
								<Input v-model="config.heroSubtitle" placeholder="Discover amazing content" />
							</div>
							<div class="grid gap-2">
								<Label>Hero Background Image</Label>
								<Input v-model="config.heroImageUrl" placeholder="https://example.com/hero.jpg" />
							</div>
						</div>

						<!-- Hero preview -->
						<Card class="overflow-hidden">
							<div
								class="relative p-8 md:p-12 text-center"
								:style="{
									backgroundColor: config.primaryColor + '15',
									backgroundImage: config.heroImageUrl ? `url(${config.heroImageUrl})` : 'none',
									backgroundSize: 'cover',
									backgroundPosition: 'center',
								}"
							>
								<div v-if="config.heroImageUrl" class="absolute inset-0 bg-black/40" />
								<div class="relative z-10">
									<h2 class="text-xl md:text-2xl font-bold" :style="{ color: config.heroImageUrl ? '#fff' : config.textColor }">
										{{ config.heroTitle || 'Your Hero Title' }}
									</h2>
									<p class="mt-2 text-sm" :style="{ color: config.heroImageUrl ? 'rgba(255,255,255,0.8)' : config.textColor + '99' }">
										{{ config.heroSubtitle || 'Your subtitle here' }}
									</p>
								</div>
							</div>
						</Card>
					</template>
				</div>

				<!-- ═══ Step 3: Gallery ═══ -->
				<div v-if="currentStep === 3" class="max-w-2xl mx-auto space-y-6">
					<div>
						<h2 class="text-lg font-medium mb-1">Gallery</h2>
						<p class="text-sm text-muted-foreground">How your media content is displayed to visitors.</p>
					</div>

					<div class="flex items-center justify-between">
						<Label>Enable Gallery Section</Label>
						<Switch v-model:checked="config.galleryEnabled" />
					</div>

					<template v-if="config.galleryEnabled">
						<div class="space-y-4">
							<div class="grid gap-2">
								<Label>Layout Style</Label>
								<div class="grid grid-cols-3 gap-3">
									<button
										v-for="opt in layoutOptions"
										:key="opt.value"
										class="p-3 border rounded-lg text-center text-sm transition-all"
										:class="config.galleryLayout === opt.value ? 'border-primary bg-primary/5 font-medium' : 'hover:border-primary/40'"
										@click="config.galleryLayout = opt.value as 'grid' | 'masonry' | 'carousel'"
									>
										{{ opt.label }}
									</button>
								</div>
							</div>

							<div v-if="config.galleryLayout !== 'carousel'" class="grid gap-2">
								<Label>Columns ({{ config.galleryColumns }})</Label>
								<input type="range" v-model.number="config.galleryColumns" min="1" max="6" class="w-full" />
								<div class="flex justify-between text-xs text-muted-foreground">
									<span>1</span><span>6</span>
								</div>
							</div>
						</div>

						<!-- Gallery layout preview -->
						<Card>
							<CardContent class="p-4">
								<p class="text-xs text-muted-foreground mb-2">Preview</p>
								<div v-if="config.galleryLayout === 'carousel'" class="flex gap-2 overflow-hidden">
									<div v-for="n in 4" :key="n" class="shrink-0 w-32 h-20 rounded bg-muted" />
								</div>
								<div v-else class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${config.galleryColumns}, 1fr)` }">
									<div v-for="n in config.galleryColumns * 2" :key="n"
										class="rounded bg-muted"
										:style="{ height: config.galleryLayout === 'masonry' ? (n % 3 === 0 ? '80px' : n % 2 === 0 ? '60px' : '50px') : '50px' }"
									/>
								</div>
							</CardContent>
						</Card>
					</template>
				</div>

				<!-- ═══ Step 4: Uploads ═══ -->
				<div v-if="currentStep === 4" class="max-w-2xl mx-auto space-y-6">
					<div>
						<h2 class="text-lg font-medium mb-1">Upload Settings</h2>
						<p class="text-sm text-muted-foreground">Configure how visitors submit content to your site.</p>
					</div>

					<div class="flex items-center justify-between">
						<Label>Enable Uploads</Label>
						<Switch v-model:checked="config.uploadEnabled" />
					</div>

					<template v-if="config.uploadEnabled">
						<div class="space-y-4">
							<div class="grid gap-2">
								<Label>Upload Section Title</Label>
								<Input v-model="config.uploadTitle" placeholder="Submit Your Content" />
							</div>
							<div class="grid gap-2">
								<Label>Description / Instructions</Label>
								<Input v-model="config.uploadDescription" placeholder="Tell us about your upload guidelines..." />
							</div>
							<div class="grid gap-2">
								<Label>Max File Size (MB): {{ config.uploadMaxSize }}</Label>
								<input type="range" v-model.number="config.uploadMaxSize" min="1" max="500" class="w-full" />
								<div class="flex justify-between text-xs text-muted-foreground">
									<span>1 MB</span><span>500 MB</span>
								</div>
							</div>

							<div class="grid gap-2">
								<Label>Allowed File Types</Label>
								<div class="flex flex-wrap gap-2">
									<button
										v-for="ftype in ['image', 'video', 'audio', 'document']"
										:key="ftype"
										class="px-3 py-1.5 border rounded-md text-xs transition-all capitalize"
										:class="config.uploadAllowedTypes.includes(ftype) ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:border-primary/40'"
										@click="config.uploadAllowedTypes.includes(ftype)
											? config.uploadAllowedTypes.splice(config.uploadAllowedTypes.indexOf(ftype), 1)
											: config.uploadAllowedTypes.push(ftype)"
									>
										{{ ftype }}
									</button>
								</div>
							</div>

							<div class="flex items-center justify-between">
								<div>
									<Label>Require Login to Upload</Label>
									<p class="text-xs text-muted-foreground">Users must register before submitting</p>
								</div>
								<Switch v-model:checked="config.uploadRequireLogin" />
							</div>

							<div class="flex items-center justify-between">
								<div>
									<Label>Enable Registration</Label>
									<p class="text-xs text-muted-foreground">Allow new users to create accounts</p>
								</div>
								<Switch v-model:checked="config.registrationEnabled" />
							</div>
						</div>
					</template>
				</div>

				<!-- ═══ Step 5: Sections — Footer & Nav ═══ -->
				<div v-if="currentStep === 5" class="max-w-2xl mx-auto space-y-6">
					<div>
						<h2 class="text-lg font-medium mb-1">Sections</h2>
						<p class="text-sm text-muted-foreground">Configure navigation and footer for your site.</p>
					</div>

					<!-- Navigation -->
					<Card>
						<CardHeader class="pb-3">
							<div class="flex items-center justify-between">
								<CardTitle class="text-sm">Navigation Bar</CardTitle>
								<Switch v-model:checked="config.navigationEnabled" />
							</div>
						</CardHeader>
						<CardContent v-if="config.navigationEnabled" class="space-y-3">
							<div v-for="(item, idx) in config.navigationItems" :key="idx" class="flex items-center gap-2">
								<Input v-model="item.label" placeholder="Link label" class="flex-1" />
								<Input v-model="item.section" placeholder="Section ID" class="flex-1" />
								<Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click="removeNavItem(idx)">
									<Trash2 class="h-3.5 w-3.5" />
								</Button>
							</div>
							<Button variant="outline" size="sm" class="gap-1.5" @click="addNavItem">
								<Plus class="h-3.5 w-3.5" /> Add Link
							</Button>
						</CardContent>
					</Card>

					<!-- Footer -->
					<Card>
						<CardHeader class="pb-3">
							<div class="flex items-center justify-between">
								<CardTitle class="text-sm">Footer</CardTitle>
								<Switch v-model:checked="config.footerEnabled" />
							</div>
						</CardHeader>
						<CardContent v-if="config.footerEnabled" class="space-y-4">
							<div class="grid gap-2">
								<Label>Footer Text</Label>
								<Input v-model="config.footerText" placeholder="© 2026 Your Company. All rights reserved." />
							</div>
							<div class="space-y-2">
								<Label>Footer Links</Label>
								<div v-for="(link, idx) in config.footerLinks" :key="idx" class="flex items-center gap-2">
									<Input v-model="link.label" placeholder="Label" class="flex-1" />
									<Input v-model="link.url" placeholder="URL" class="flex-1" />
									<Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click="removeFooterLink(idx)">
										<Trash2 class="h-3.5 w-3.5" />
									</Button>
								</div>
								<Button variant="outline" size="sm" class="gap-1.5" @click="addFooterLink">
									<Plus class="h-3.5 w-3.5" /> Add Link
								</Button>
							</div>
						</CardContent>
					</Card>

					<!-- SEO -->
					<Card>
						<CardHeader class="pb-3">
							<CardTitle class="text-sm">SEO & Analytics</CardTitle>
						</CardHeader>
						<CardContent class="space-y-4">
							<div class="grid gap-2">
								<Label>SEO Title</Label>
								<Input v-model="config.seoTitle" placeholder="Page title for search engines" />
							</div>
							<div class="grid gap-2">
								<Label>SEO Description</Label>
								<Input v-model="config.seoDescription" placeholder="Brief description for search results" />
							</div>
							<div class="grid gap-2">
								<Label>Google Analytics ID</Label>
								<Input v-model="config.analyticsId" placeholder="G-XXXXXXXXXX" />
							</div>
						</CardContent>
					</Card>
				</div>

				<!-- ═══ Step 6: Publish / Review ═══ -->
				<div v-if="currentStep === 6" class="max-w-2xl mx-auto space-y-6">
					<div>
						<h2 class="text-lg font-medium mb-1">Review & Publish</h2>
						<p class="text-sm text-muted-foreground">Review your settings before going live.</p>
					</div>

					<!-- Summary cards -->
					<div class="grid gap-4 sm:grid-cols-2">
						<Card>
							<CardContent class="p-4">
								<div class="flex items-center gap-2 mb-2">
									<LayoutTemplate class="h-4 w-4 text-muted-foreground" />
									<span class="text-sm font-medium">Template</span>
								</div>
								<p class="text-sm text-muted-foreground capitalize">{{ config.template || 'None selected' }}</p>
							</CardContent>
						</Card>
						<Card>
							<CardContent class="p-4">
								<div class="flex items-center gap-2 mb-2">
									<Palette class="h-4 w-4 text-muted-foreground" />
									<span class="text-sm font-medium">Branding</span>
								</div>
								<div class="flex items-center gap-2">
									<div class="h-5 w-5 rounded border" :style="{ backgroundColor: config.primaryColor }" />
									<span class="text-sm text-muted-foreground">{{ config.siteName || 'Not set' }}</span>
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent class="p-4">
								<div class="flex items-center gap-2 mb-2">
									<Image class="h-4 w-4 text-muted-foreground" />
									<span class="text-sm font-medium">Gallery</span>
								</div>
								<Badge :variant="config.galleryEnabled ? 'default' : 'secondary'">
									{{ config.galleryEnabled ? config.galleryLayout + ' layout' : 'Disabled' }}
								</Badge>
							</CardContent>
						</Card>
						<Card>
							<CardContent class="p-4">
								<div class="flex items-center gap-2 mb-2">
									<Upload class="h-4 w-4 text-muted-foreground" />
									<span class="text-sm font-medium">Uploads</span>
								</div>
								<Badge :variant="config.uploadEnabled ? 'default' : 'secondary'">
									{{ config.uploadEnabled ? 'Enabled' : 'Disabled' }}
								</Badge>
							</CardContent>
						</Card>
					</div>

					<!-- Enabled sections list -->
					<Card>
						<CardHeader class="pb-2">
							<CardTitle class="text-sm">Active Sections</CardTitle>
						</CardHeader>
						<CardContent>
							<div class="flex flex-wrap gap-2">
								<Badge v-if="config.heroEnabled" variant="outline">Hero</Badge>
								<Badge v-if="config.galleryEnabled" variant="outline">Gallery</Badge>
								<Badge v-if="config.uploadEnabled" variant="outline">Uploads</Badge>
								<Badge v-if="config.navigationEnabled" variant="outline">Navigation</Badge>
								<Badge v-if="config.footerEnabled" variant="outline">Footer</Badge>
								<Badge v-if="config.registrationEnabled" variant="outline">Registration</Badge>
							</div>
						</CardContent>
					</Card>

					<div class="flex gap-3">
						<Button variant="outline" class="flex-1 gap-1.5" @click="saveConfig" :disabled="saving">
							<Save class="h-4 w-4" /> Save Draft
						</Button>
						<Button class="flex-1 gap-1.5" @click="publishApp" :disabled="saving">
							<Loader2 v-if="saving" class="h-4 w-4 animate-spin" />
							<Globe v-else class="h-4 w-4" />
							Publish
						</Button>
					</div>
				</div>
			</div>

			<!-- Footer navigation -->
			<div class="flex items-center justify-between px-3 md:px-6 py-3 border-t bg-background shrink-0">
				<Button
					variant="outline"
					:disabled="currentStep === 0"
					@click="currentStep--"
					class="gap-1.5"
				>
					<ChevronLeft class="h-4 w-4" />
					<span class="hidden sm:inline">Back</span>
				</Button>
				<span class="text-xs text-muted-foreground">
					Step {{ currentStep + 1 }} of {{ steps.length }}
				</span>
				<Button
					v-if="currentStep < steps.length - 1"
					:disabled="!canGoNext"
					@click="currentStep++"
					class="gap-1.5"
				>
					<span class="hidden sm:inline">Next</span>
					<ChevronRight class="h-4 w-4" />
				</Button>
				<Button
					v-else
					class="gap-1.5"
					@click="publishApp"
					:disabled="saving"
				>
					<Loader2 v-if="saving" class="h-4 w-4 animate-spin" />
					<Globe v-else class="h-4 w-4" />
					Publish
				</Button>
			</div>
		</template>
	</div>
</template>
